/**
 * TeamDetailDrawer — right slide-over showing a Team's memberships, with
 * promote/demote/remove actions.
 *
 * Per TEAMS-SPEC.md:
 *   - DEPARTMENT_HEAD section is read-only (managed via Departments).
 *   - MANAGERS + EXECUTIVES lists support promote/demote and remove.
 *   - Edit + Deactivate buttons in header (Deactivate = admin only).
 *   - Uses warm-ivory tokens only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import {
  X,
  Loader2,
  MoreVertical,
  Pencil,
  UserMinus,
  ChevronUp,
  ChevronDown,
  Plus,
  AlertCircle,
} from 'lucide-react';
import {
  getTeam,
  updateTeam,
  addMember,
  updateMember,
  removeMember,
  type Team,
  type TeamMembership,
  type TeamMemberLevel,
} from '@/api/teams';
import { useTeamPermissions } from '@/hooks/useTeamPermissions';
import { TeamMemberPicker } from './TeamMemberPicker';

interface Props {
  open: boolean;
  teamId: string | null;
  onClose: () => void;
  onEdit: (team: Team) => void;
  /** Called after any mutation so the parent list can refresh. */
  onChange?: () => void;
}

export function TeamDetailDrawer({
  open,
  teamId,
  onClose,
  onEdit,
  onChange,
}: Props) {
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [pickerLevel, setPickerLevel] = useState<
    'MANAGER' | 'EXECUTIVE' | null
  >(null);
  const [busy, setBusy] = useState(false);

  const perms = useTeamPermissions(team?.departmentId);

  const load = useCallback(async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      setError(null);
      const t = await getTeam(teamId);
      setTeam(t);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load team.');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (open && teamId) load();
    if (!open) {
      setTeam(null);
      setMenuOpenId(null);
      setPickerLevel(null);
      setError(null);
    }
  }, [open, teamId, load]);

  const activeMemberships = useMemo(
    () => (team?.memberships ?? []).filter((m) => !m.leftAt),
    [team],
  );

  const head = activeMemberships.find((m) => m.level === 'DEPARTMENT_HEAD');
  const managers = activeMemberships.filter((m) => m.level === 'MANAGER');
  const executives = activeMemberships.filter((m) => m.level === 'EXECUTIVE');

  const handleDeactivate = async () => {
    if (!team) return;
    if (!confirm(`Deactivate team "${team.name}"? You can reactivate later.`))
      return;
    try {
      setBusy(true);
      await updateTeam(team.id, { isActive: false });
      onChange?.();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to deactivate.');
    } finally {
      setBusy(false);
    }
  };

  const handleReactivate = async () => {
    if (!team) return;
    try {
      setBusy(true);
      await updateTeam(team.id, { isActive: true });
      await load();
      onChange?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to reactivate.');
    } finally {
      setBusy(false);
    }
  };

  const handlePromoteDemote = async (
    membership: TeamMembership,
    next: 'MANAGER' | 'EXECUTIVE',
  ) => {
    if (!team) return;
    try {
      setBusy(true);
      setMenuOpenId(null);
      await updateMember(team.id, membership.id, { level: next });
      await load();
      onChange?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update member.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (membership: TeamMembership) => {
    if (!team) return;
    if (membership.level === 'DEPARTMENT_HEAD') return; // Guarded in UI.
    if (
      !confirm(
        `Remove ${membership.user.firstName} ${membership.user.lastName} from this team?`,
      )
    )
      return;
    try {
      setBusy(true);
      setMenuOpenId(null);
      await removeMember(team.id, membership.id);
      await load();
      onChange?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to remove member.');
    } finally {
      setBusy(false);
    }
  };

  const handlePick = async (userIds: string[]) => {
    if (!team || !pickerLevel) return;
    try {
      setBusy(true);
      // Loop sequentially so 409s don't abort the whole batch — keep errors
      // in memory and surface only the last one.
      let lastErr: string | null = null;
      for (const userId of userIds) {
        try {
          await addMember(team.id, { userId, level: pickerLevel });
        } catch (err: any) {
          lastErr =
            err?.response?.data?.error
            || `Failed to add user ${userId}.`;
        }
      }
      await load();
      onChange?.();
      if (lastErr) setError(lastErr);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Modal
        open
        side="right"
        onClose={onClose}
        ariaLabel="Team details"
        className="aura-root !max-w-[520px] !border-0 !border-l !border-[var(--line)] !bg-[var(--ivory-1)] !shadow-[var(--sh-4)]"
      >
      <div className="flex h-full flex-col">
        {/* Header */}
        <div
          className="flex items-start justify-between px-5 py-4"
          style={{ borderBottom: '1px solid var(--line)', gap: 12 }}
        >
          <div className="grow" style={{ minWidth: 0 }}>
            {loading || !team ? (
              <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>
                Loading team…
              </div>
            ) : (
              <>
                <div className="row center gap-2" style={{ flexWrap: 'wrap' }}>
                  <h2
                    style={{
                      fontFamily: 'var(--ff-serif)',
                      fontSize: 22,
                      color: 'var(--ink-0)',
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {team.name}
                  </h2>
                  {team.code && (
                    <span className="chip chip-amber mono">{team.code}</span>
                  )}
                  {team.department && (
                    <span className="chip">{team.department.name}</span>
                  )}
                  {!team.isActive && <span className="chip chip-red">Inactive</span>}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--ink-3)',
                    marginTop: 4,
                  }}
                >
                  {activeMemberships.length} member
                  {activeMemberships.length === 1 ? '' : 's'} · Created{' '}
                  {new Date(team.createdAt).toLocaleDateString()}
                </div>
                {team.description && (
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--ink-1)',
                      marginTop: 8,
                    }}
                  >
                    {team.description}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="row gap-1" style={{ flexShrink: 0 }}>
            {team && perms.canEdit && (
              <button
                className="btn btn-outline btn-sm"
                onClick={() => onEdit(team)}
                title="Edit team"
              >
                <Pencil size={12} /> Edit
              </button>
            )}
            {team && perms.canDelete && team.isActive && (
              <button
                className="btn btn-outline btn-sm"
                onClick={handleDeactivate}
                disabled={busy}
                style={{ color: 'var(--danger)' }}
              >
                Deactivate
              </button>
            )}
            {team && perms.canDelete && !team.isActive && (
              <button
                className="btn btn-outline btn-sm"
                onClick={handleReactivate}
                disabled={busy}
              >
                Reactivate
              </button>
            )}
            <CloseBtn />
          </div>
        </div>

        {/* Error strip */}
        {error && (
          <div
            className="row center gap-2 px-5 py-2"
            style={{
              background: 'var(--danger-bg)',
              color: 'var(--danger)',
              fontSize: 12,
              borderBottom: '1px solid var(--line)',
            }}
          >
            <AlertCircle size={12} />
            {error}
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setError(null)}
              style={{ marginLeft: 'auto' }}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4" style={{ minHeight: 0 }}>
          {loading || !team ? (
            <div
              className="row center"
              style={{
                justifyContent: 'center',
                padding: 40,
                color: 'var(--ink-3)',
                gap: 8,
              }}
            >
              <Loader2 size={16} className="animate-spin" />
              Loading…
            </div>
          ) : (
            <>
              {/* DEPARTMENT_HEAD */}
              <Section title="Department Head" count={head ? 1 : 0}>
                {head ? (
                  <MemberRow
                    membership={head}
                    locked
                    onMenuToggle={() => {}}
                    menuOpen={false}
                    canEdit={false}
                    busy={busy}
                    onPromote={() => {}}
                    onDemote={() => {}}
                    onRemove={() => {}}
                  />
                ) : (
                  <EmptyNote>
                    No Department Head set. Assign one in{' '}
                    <strong>Departments</strong>.
                  </EmptyNote>
                )}
              </Section>

              {/* MANAGERS */}
              <Section
                title="Managers"
                count={managers.length}
                action={
                  perms.canEdit ? (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setPickerLevel('MANAGER')}
                      disabled={!team.isActive}
                    >
                      <Plus size={12} /> Add Manager
                    </button>
                  ) : null
                }
              >
                {managers.length === 0 ? (
                  <EmptyNote>No managers yet.</EmptyNote>
                ) : (
                  managers.map((m) => (
                    <MemberRow
                      key={m.id}
                      membership={m}
                      locked={false}
                      onMenuToggle={() =>
                        setMenuOpenId(menuOpenId === m.id ? null : m.id)
                      }
                      menuOpen={menuOpenId === m.id}
                      canEdit={perms.canEdit && team.isActive}
                      busy={busy}
                      onPromote={() => handlePromoteDemote(m, 'MANAGER')}
                      onDemote={() => handlePromoteDemote(m, 'EXECUTIVE')}
                      onRemove={() => handleRemove(m)}
                    />
                  ))
                )}
              </Section>

              {/* EXECUTIVES */}
              <Section
                title="Executives"
                count={executives.length}
                action={
                  perms.canEdit ? (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setPickerLevel('EXECUTIVE')}
                      disabled={!team.isActive}
                    >
                      <Plus size={12} /> Add Executive
                    </button>
                  ) : null
                }
              >
                {executives.length === 0 ? (
                  <EmptyNote>No executives yet.</EmptyNote>
                ) : (
                  executives.map((m) => (
                    <MemberRow
                      key={m.id}
                      membership={m}
                      locked={false}
                      onMenuToggle={() =>
                        setMenuOpenId(menuOpenId === m.id ? null : m.id)
                      }
                      menuOpen={menuOpenId === m.id}
                      canEdit={perms.canEdit && team.isActive}
                      busy={busy}
                      onPromote={() => handlePromoteDemote(m, 'MANAGER')}
                      onDemote={() => handlePromoteDemote(m, 'EXECUTIVE')}
                      onRemove={() => handleRemove(m)}
                    />
                  ))
                )}
              </Section>
            </>
          )}
        </div>
      </div>
      </Modal>

      {/* Member picker */}
      {team && (
        <TeamMemberPicker
          open={!!pickerLevel}
          onClose={() => setPickerLevel(null)}
          teamDepartmentId={team.departmentId}
          teamDepartmentName={team.department?.name ?? 'Department'}
          level={pickerLevel ?? 'EXECUTIVE'}
          existingMemberships={team.memberships ?? []}
          onPick={handlePick}
        />
      )}
    </>
  );
}

// ── Internal atoms ──────────────────────────────────────────────────────────
function CloseBtn() {
  const { requestClose } = useModalClose();
  return (
    <button type="button" className="btn btn-ghost" onClick={requestClose} aria-label="Close">
      <X size={16} />
    </button>
  );
}

function Section({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: 24 }}>
      <div
        className="row center between"
        style={{
          marginBottom: 10,
          paddingBottom: 6,
          borderBottom: '1px solid var(--line)',
        }}
      >
        <div className="row center gap-2">
          <h3
            style={{
              fontFamily: 'var(--ff-sans)',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              margin: 0,
            }}
          >
            {title}
          </h3>
          <span
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
            }}
          >
            · {count}
          </span>
        </div>
        {action}
      </div>
      <div style={{ display: 'grid', gap: 4 }}>{children}</div>
    </section>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 'var(--r-sm)',
        background: 'var(--ivory-2)',
        border: '1px dashed var(--line)',
        fontSize: 12,
        color: 'var(--ink-3)',
      }}
    >
      {children}
    </div>
  );
}

interface MemberRowProps {
  membership: TeamMembership;
  locked: boolean;
  canEdit: boolean;
  busy: boolean;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onPromote: () => void;
  onDemote: () => void;
  onRemove: () => void;
}

function MemberRow({
  membership,
  locked,
  canEdit,
  busy,
  menuOpen,
  onMenuToggle,
  onPromote,
  onDemote,
  onRemove,
}: MemberRowProps) {
  const { user, level } = membership;
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email;
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? '')
    .join('');

  const chipClass = levelChip(level);

  return (
    <div
      className="row center"
      style={{
        padding: '8px 12px',
        borderRadius: 'var(--r-sm)',
        background: 'var(--ivory-0)',
        border: '1px solid var(--line)',
        gap: 10,
        position: 'relative',
        minHeight: 52,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'var(--ivory-2)',
          border: '1px solid var(--line)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--ink-1)',
          flexShrink: 0,
        }}
      >
        {initials || '?'}
      </div>
      <div className="grow" style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--ink-0)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--ink-3)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {user.designation || user.email}
        </div>
      </div>
      <span className={chipClass}>{levelLabel(level)}</span>
      {locked ? (
        <span
          style={{
            fontSize: 10,
            color: 'var(--ink-3)',
            fontStyle: 'italic',
            marginLeft: 4,
          }}
          title="Managed via Departments"
        >
          Managed via Departments
        </span>
      ) : canEdit ? (
        <div style={{ position: 'relative' }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onMenuToggle}
            disabled={busy}
            aria-label="Member actions"
          >
            <MoreVertical size={14} />
          </button>
          {menuOpen && (
            <div className="status-menu" style={{ right: 0, top: 28 }}>
              {level === 'EXECUTIVE' ? (
                <button onClick={onPromote} disabled={busy}>
                  <ChevronUp size={12} /> Promote to Manager
                </button>
              ) : (
                <button onClick={onDemote} disabled={busy}>
                  <ChevronDown size={12} /> Demote to Executive
                </button>
              )}
              <button
                onClick={onRemove}
                disabled={busy}
                style={{ color: 'var(--danger)' }}
              >
                <UserMinus size={12} /> Remove from team
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function levelChip(level: TeamMemberLevel): string {
  if (level === 'DEPARTMENT_HEAD') return 'chip chip-level-head';
  if (level === 'MANAGER') return 'chip chip-level-manager';
  return 'chip chip-level-executive';
}

function levelLabel(level: TeamMemberLevel): string {
  if (level === 'DEPARTMENT_HEAD') return 'Dept. Head';
  if (level === 'MANAGER') return 'Manager';
  return 'Executive';
}
